import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Shield, ShieldAlert, X, Search, Globe, Ban, ShieldOff, Smartphone, Monitor, Tablet, Bell, Download, Trash2, Activity } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { flagEmoji, fmtTime } from "@/lib/regie";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ALL_COUNTRIES, COUNTRY_NAME_BY_CODE } from "@/lib/countries";
import { BotBadge } from "@/pages/admin/SuperRegie";

const countryByCode = COUNTRY_NAME_BY_CODE;

export default function AdminSecurity() {
  const { user } = useAuth();

  // Turnstile
  const [ts, setTs] = useState({ enabled: false, site_key: "", secret_key: "" });
  const [tsSaving, setTsSaving] = useState(false);

  // Country blocks
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [countryMode, setCountryMode] = useState("block"); // block = liste noire | allow = liste blanche
  const [exemptCustomers, setExemptCustomers] = useState(true);
  // Vue d'ensemble, alertes, rétention
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [retention, setRetention] = useState(null);
  const [retSaving, setRetSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [cbSaving, setCbSaving] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // IP rules (root only)
  const [ipRules, setIpRules] = useState({ enabled: false, max_sessions: 200, window_hours: 24 });
  const [ipRulesSaving, setIpRulesSaving] = useState(false);

  // Manual IP blocks
  const [ipBlocks, setIpBlocks] = useState([]);
  const [ipBlocksLoading, setIpBlocksLoading] = useState(true);
  const [unblockingIp, setUnblockingIp] = useState(null);
  // Appareils bloqués (identifiants de session)
  const [sessionBlocks, setSessionBlocks] = useState([]);
  const [sessionBlocksLoading, setSessionBlocksLoading] = useState(true);
  const [unblockingSid, setUnblockingSid] = useState(null);

  useEffect(() => {
    if (user?.is_root) {
      api.get("/root/turnstile").then(({ data }) => setTs(data)).catch(() => {});
      api.get("/root/country-blocks").then(({ data }) => { setBlockedCountries(data.countries || []); setCountryMode(data.mode || "block"); setExemptCustomers(data.exempt_customers !== false); }).catch(() => {});
      api.get("/root/security/overview", { params: { days: 30 } }).then(({ data }) => setOverview(data)).catch(() => {});
      api.get("/root/security/alerts", { params: { limit: 50 } }).then(({ data }) => setAlerts(data.alerts || [])).catch(() => {});
      api.get("/root/retention").then(({ data }) => setRetention(data)).catch(() => {});
      api.get("/root/ip-rules").then(({ data }) => setIpRules(data)).catch(() => {});
      api.get("/superadmin/ip-blocks").then(({ data }) => setIpBlocks(data.ips || [])).catch(() => {}).finally(() => setIpBlocksLoading(false));
      api.get("/superadmin/session-blocks").then(({ data }) => setSessionBlocks(data.sessions || [])).catch(() => {}).finally(() => setSessionBlocksLoading(false));
    }
  }, [user]);

  const markAlertsRead = async (ids) => {
    try {
      await api.put("/root/security/alerts/read", { ids: ids || null });
      setAlerts((list) => list.map((a) => (!ids || ids.includes(a.id) ? { ...a, read: true } : a)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const saveRetention = async () => {
    setRetSaving(true);
    try {
      const { data } = await api.put("/root/retention", { days: Number(retention.days), sessions_days: Number(retention.sessions_days) });
      setRetention(data); toast.success("Rétention enregistrée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRetSaving(false);
    }
  };
  const purgeNow = async () => {
    if (!window.confirm("Purger maintenant les données plus anciennes que la rétention configurée ?")) return;
    setPurging(true);
    try {
      const { data } = await api.post("/root/retention/purge");
      toast.success("Purge effectuée : " + Object.entries(data.result || {}).map(([k, v]) => `${k} ${v}`).join(", "));
      api.get("/root/retention").then(({ data: r }) => setRetention(r)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPurging(false);
    }
  };
  const downloadCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export impossible");
    }
  };

  const unblockDevice = async (sid) => {
    setUnblockingSid(sid);
    try {
      await api.delete(`/superadmin/session-blocks/${encodeURIComponent(sid)}`);
      setSessionBlocks((list) => list.filter((e) => e.session_id !== sid));
      toast.success("Appareil débloqué");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUnblockingSid(null);
    }
  };

  const filteredCountries = useMemo(() => {
    const q = countrySearch.toLowerCase();
    return ALL_COUNTRIES.filter(
      (c) => !blockedCountries.includes(c.code) && (c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q))
    ).slice(0, 30);
  }, [countrySearch, blockedCountries]);

  if (!user?.is_root) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-copper" strokeWidth={1.2} />
        <h1 className="font-serif text-3xl text-ink">Accès réservé</h1>
        <p className="text-graphite max-w-sm">Ce module est réservé au compte racine.</p>
      </div>
    );
  }

  const saveTurnstile = async () => {
    setTsSaving(true);
    try {
      const { data } = await api.put("/root/turnstile", ts);
      setTs(data);
      toast.success("Configuration Turnstile enregistrée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTsSaving(false);
    }
  };

  const addCountry = (code) => {
    if (!blockedCountries.includes(code)) {
      setBlockedCountries((prev) => [...prev, code]);
    }
    setCountrySearch("");
    setShowDropdown(false);
  };

  const removeCountry = (code) => {
    setBlockedCountries((prev) => prev.filter((c) => c !== code));
  };

  const saveCountryBlocks = async () => {
    setCbSaving(true);
    try {
      const { data } = await api.put("/root/country-blocks", { countries: blockedCountries, mode: countryMode, exempt_customers: exemptCustomers });
      setBlockedCountries(data.countries); setCountryMode(data.mode || "block"); setExemptCustomers(data.exempt_customers !== false);
      toast.success("Blocage géographique enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCbSaving(false);
    }
  };

  const saveIpRules = async () => {
    setIpRulesSaving(true);
    try {
      const { data } = await api.put("/root/ip-rules", ipRules);
      setIpRules(data);
      toast.success("Règles IP enregistrées");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setIpRulesSaving(false);
    }
  };

  const unblockIp = async (ip) => {
    setUnblockingIp(ip);
    try {
      const { data } = await api.delete(`/superadmin/ip-blocks/${encodeURIComponent(ip)}`);
      setIpBlocks(data.ips || []);
      toast.success(`IP ${ip} débloquée`);
      api.get("/superadmin/session-blocks").then(({ data: d }) => setSessionBlocks(d.sessions || [])).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUnblockingIp(null);
    }
  };

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
          <Shield className="h-4 w-4 text-gold" /> Compte racine
        </p>
        <h1 className="font-serif text-4xl text-ink tracking-tight">Sécurité</h1>
        <p className="text-graphite mt-2">
          Protection anti-bot, blocage géographique, blocages IP / appareils, règles automatiques.
        </p>
      </div>

      {/* ── Vue d'ensemble 30 jours ── */}
      {overview && !overview.error && (
        <div className="bg-white border border-stone rounded-sm p-6 space-y-5" data-testid="security-overview">
          <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-copper" strokeWidth={1.5} /><h2 className="font-serif text-xl text-ink">Vue d'ensemble — 30 jours</h2></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { l: "Requêtes refusées", v: overview.refused_total, c: "text-red-600" },
              { l: "Appareils nomades", v: overview.nomad_devices, c: "text-sky-700" },
              { l: "Appareils multi-pays", v: overview.multi_country_devices, c: "text-amber-700" },
              { l: "Faux robots", v: overview.bots?.impostor || 0, c: "text-red-600" },
              { l: "Robots vérifiés", v: overview.bots?.verified || 0, c: "text-emerald-700" },
              { l: "Autres robots", v: overview.bots?.bot || 0, c: "text-ink" },
              { l: "Visiteurs humains", v: overview.humans, c: "text-ink" },
              { l: "Alertes non lues", v: overview.alerts_unread, c: overview.alerts_unread ? "text-red-600" : "text-ink" },
            ].map((s) => (
              <div key={s.l} className="bg-sand/40 border border-stone rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash">{s.l}</p>
                <p className={`font-serif text-2xl mt-0.5 ${s.c}`}>{s.v ?? 0}</p>
              </div>
            ))}
          </div>
          <div className="text-xs text-graphite flex flex-wrap gap-x-4 gap-y-1">
            <span>Blocages actifs : <strong>{overview.active_blocks?.ips}</strong> IP · <strong>{overview.active_blocks?.devices}</strong> appareils · <strong>{overview.active_blocks?.countries}</strong> pays ({overview.active_blocks?.country_mode === "allow" ? "liste blanche" : "liste noire"})</span>
            <span>Règles automatiques : <strong>{overview.active_blocks?.rules}</strong> actives · moteur {overview.active_blocks?.autoblock_enabled ? "activé" : "désactivé"}{overview.active_blocks?.dry_run ? " · simulation" : ""}</span>
          </div>
          {overview.refused_series?.some((d) => d.total > 0) && (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={overview.refused_series} margin={{ left: -20, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE5DC" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8782" }} tickFormatter={(x) => x.slice(8)} interval={4} />
                <YAxis tick={{ fontSize: 10, fill: "#8A8782" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #EAE5DC", fontSize: 12 }} />
                <Bar dataKey="ip" stackId="a" fill="#b91c1c" name="IP" />
                <Bar dataKey="country" stackId="a" fill="#C9A36A" name="Pays" />
                <Bar dataKey="session" stackId="a" fill="#0369a1" name="Appareil" />
                <Bar dataKey="suspicious" stackId="a" fill="#5C5547" name="Chemins suspects" />
                <Bar dataKey="auto" stackId="a" fill="#b45309" name="Règles auto" />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="grid md:grid-cols-3 gap-4 text-xs">
            <div>
              <p className="text-[10px] uppercase tracking-[0.14em] text-ash mb-1">Par motif</p>
              {(overview.refused_by_reason || []).map((b) => <div key={b.reason} className="flex justify-between"><span className="text-graphite">{({ ip: "Blocage IP", country: "Pays", session: "Appareil", suspicious: "Chemins suspects", auto: "Règle automatique" })[b.reason] || b.reason}</span><span className="font-mono text-ink">{b.count}</span></div>)}
              {!overview.refused_by_reason?.length && <p className="text-ash italic">Aucun refus</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.14em] text-ash mb-1">Pays les plus refusés</p>
              {(overview.top_countries_refused || []).map((b) => <div key={b.country_code} className="flex justify-between"><span className="text-graphite">{flagEmoji(b.country_code)} {COUNTRY_NAME_BY_CODE[b.country_code] || b.country_code}</span><span className="font-mono text-ink">{b.count}</span></div>)}
              {!overview.top_countries_refused?.length && <p className="text-ash italic">—</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.14em] text-ash mb-1">Règles les plus déclenchées</p>
              {(overview.top_rules || []).map((b) => <div key={b.rule} className="flex justify-between"><span className="text-graphite truncate pr-2">{b.rule}</span><span className="font-mono text-ink">{b.blocks}/{b.count}</span></div>)}
              {!overview.top_rules?.length && <p className="text-ash italic">—</p>}
            </div>
          </div>
        </div>
      )}

      {/* ── Alertes ── */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-4" data-testid="security-alerts">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-copper" strokeWidth={1.5} /><h2 className="font-serif text-xl text-ink">Alertes</h2>
            {alerts.filter((a) => !a.read).length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-600 text-white">{alerts.filter((a) => !a.read).length}</span>}
          </div>
          {alerts.some((a) => !a.read) && <button onClick={() => markAlertsRead(null)} className="text-xs uppercase tracking-[0.1em] border border-stone bg-white px-3 py-1.5 text-graphite hover:bg-sand rounded-sm">Tout marquer lu</button>}
        </div>
        <p className="text-sm text-graphite">Appareil bloqué revenu depuis une nouvelle IP, faux robot détecté, blocage automatique appliqué. Les alertes importantes sont aussi envoyées par e-mail aux adresses d'administration configurées dans Communication.</p>
        {alerts.length === 0 ? (
          <p className="text-sm text-ash italic">Aucune alerte.</p>
        ) : (
          <div className="divide-y divide-stone border border-stone rounded-sm overflow-hidden max-h-80 overflow-y-auto">
            {alerts.map((a) => (
              <div key={a.id} className={`px-4 py-2.5 text-xs flex items-start gap-3 ${a.read ? "bg-white opacity-70" : "bg-red-50/50"}`}>
                <ShieldAlert className={`h-4 w-4 shrink-0 mt-0.5 ${a.read ? "text-ash" : "text-red-600"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-ink font-medium">{a.title}</p>
                  <p className="text-graphite">{a.detail}</p>
                  <p className="text-ash mt-0.5 font-mono">{a.ip || ""}{a.country ? ` · ${flagEmoji(a.country)} ${a.country}` : ""} · {fmtTime(a.at)}</p>
                </div>
                {!a.read && <button onClick={() => markAlertsRead([a.id])} className="text-[10px] uppercase text-ash hover:text-ink shrink-0">Lu</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Cloudflare Turnstile ── */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-5">
        <div>
          <h2 className="font-serif text-xl text-ink">Cloudflare Turnstile</h2>
          <p className="text-sm text-graphite mt-1">
            Protection anti-bot sur la page de connexion. La clé publique s&apos;affiche dans le widget,
            la clé secrète reste côté serveur uniquement.
          </p>
        </div>

        <div className="flex items-center justify-between py-3 border-y border-stone">
          <div>
            <p className="text-sm font-medium text-ink">Activer sur la page de connexion</p>
            <p className="text-xs text-ash mt-0.5">Les utilisateurs devront valider le widget pour se connecter.</p>
          </div>
          <Switch
            checked={ts.enabled}
            onCheckedChange={(v) => setTs((p) => ({ ...p, enabled: v }))}
            className="data-[state=checked]:bg-gold"
          />
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.12em] text-ash block mb-1.5">
              Site Key <span className="normal-case tracking-normal text-graphite">(publique)</span>
            </label>
            <input
              type="text"
              value={ts.site_key}
              onChange={(e) => setTs((p) => ({ ...p, site_key: e.target.value }))}
              placeholder="0x4AAAAAAA..."
              className="w-full border border-stone rounded-sm px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:border-copper transition-colors font-mono"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.12em] text-ash block mb-1.5">
              Secret Key <span className="normal-case tracking-normal text-graphite">(privée — jamais exposée au client)</span>
            </label>
            <input
              type="password"
              value={ts.secret_key}
              onChange={(e) => setTs((p) => ({ ...p, secret_key: e.target.value }))}
              placeholder="0x4AAAAAAA..."
              className="w-full border border-stone rounded-sm px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:border-copper transition-colors font-mono"
            />
          </div>
        </div>

        <button
          onClick={saveTurnstile}
          disabled={tsSaving}
          className="bg-ink text-cream px-6 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-graphite transition-colors disabled:opacity-50"
        >
          {tsSaving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {/* ── Blocage géographique ── */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Globe className="h-4 w-4 text-copper" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-ink">Blocage géographique</h2>
          </div>
          <p className="text-sm text-graphite">
            {countryMode === "allow"
              ? "Mode liste blanche : seuls les visiteurs localisés dans les pays listés peuvent accéder au site. Un pays inconnu est laissé passer."
              : "Mode liste noire : les visiteurs dont l'IP est localisée dans un pays bloqué reçoivent une erreur 403."}
            {" "}Les routes d&apos;administration ne sont pas affectées, et les robots Google / Bing vérifiés jamais bloqués.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="border border-stone rounded-sm p-3">
            <p className="text-xs uppercase tracking-[0.12em] text-ash mb-1.5">Mode</p>
            <select value={countryMode} onChange={(e) => setCountryMode(e.target.value)} data-testid="country-mode" className="w-full border border-stone rounded-sm px-3 py-2 text-sm bg-white">
              <option value="block">Bloquer les pays listés (liste noire)</option>
              <option value="allow">Autoriser SEULEMENT les pays listés (liste blanche)</option>
            </select>
          </div>
          <div className="border border-stone rounded-sm p-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-ash">Clients connectés exemptés</p>
              <p className="text-[11px] text-graphite mt-0.5">Un client identifié n'est pas coupé en voyage.</p>
            </div>
            <Switch checked={exemptCustomers} onCheckedChange={setExemptCustomers} data-testid="country-exempt-customers" />
          </div>
        </div>

        {/* Pays bloqués actuels */}
        {blockedCountries.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.12em] text-ash">{countryMode === "allow" ? "Pays autorisés" : "Pays bloqués"}</p>
            <div className="flex flex-wrap gap-2">
              {blockedCountries.map((code) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-700 text-xs px-2.5 py-1 rounded-sm"
                >
                  <span className="font-mono font-medium">{code}</span>
                  <span className="text-red-500">·</span>
                  <span>{countryByCode[code] || code}</span>
                  <button
                    onClick={() => removeCountry(code)}
                    className="ml-0.5 text-red-400 hover:text-red-700 transition-colors"
                    title="Retirer ce pays"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-ash italic">Aucun pays bloqué — accès mondial autorisé.</p>
        )}

        {/* Recherche / ajout pays */}
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.12em] text-ash mb-1.5">Ajouter un pays</p>
          <div className="relative">
            <Search className="h-4 w-4 text-ash absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={countrySearch}
              onChange={(e) => { setCountrySearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Rechercher un pays (ex : Maroc, DE…)"
              className="w-full border border-stone rounded-sm pl-9 pr-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:border-copper transition-colors"
            />
          </div>
          {showDropdown && filteredCountries.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-stone rounded-sm shadow-md max-h-52 overflow-y-auto">
              {filteredCountries.map((c) => (
                <button
                  key={c.code}
                  onMouseDown={() => addCountry(c.code)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-sand transition-colors flex items-center gap-2"
                >
                  <span className="font-mono text-xs text-ash w-8">{c.code}</span>
                  <span className="text-ink">{c.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={saveCountryBlocks}
          disabled={cbSaving}
          className="bg-ink text-cream px-6 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-graphite transition-colors disabled:opacity-50"
        >
          {cbSaving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {/* ── Règles de blocage IP automatique ── */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Ban className="h-4 w-4 text-copper" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-ink">Règles de blocage IP</h2>
          </div>
          <p className="text-sm text-graphite">
            Seuil de sessions par IP sur une fenêtre de temps. Visible dans <strong>Stats IP</strong> (Visiteurs organiques) pour bloquer manuellement les IPs suspectes.
          </p>
        </div>

        <div className="flex items-center justify-between py-3 border-y border-stone">
          <div>
            <p className="text-sm font-medium text-ink">Afficher le seuil dans les stats IP</p>
            <p className="text-xs text-ash mt-0.5">Le seuil est indicatif — le blocage reste manuel depuis la page Stats IP.</p>
          </div>
          <Switch
            checked={ipRules.enabled}
            onCheckedChange={(v) => setIpRules((p) => ({ ...p, enabled: v }))}
            className="data-[state=checked]:bg-gold"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.12em] text-ash block mb-1.5">
              Sessions max
            </label>
            <input
              type="number"
              min="1"
              value={ipRules.max_sessions}
              onChange={(e) => setIpRules((p) => ({ ...p, max_sessions: Number(e.target.value) }))}
              className="w-full border border-stone rounded-sm px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:border-copper transition-colors"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.12em] text-ash block mb-1.5">
              Fenêtre (heures)
            </label>
            <input
              type="number"
              min="1"
              value={ipRules.window_hours}
              onChange={(e) => setIpRules((p) => ({ ...p, window_hours: Number(e.target.value) }))}
              className="w-full border border-stone rounded-sm px-3 py-2.5 text-sm text-ink bg-white focus:outline-none focus:border-copper transition-colors"
            />
          </div>
        </div>

        <button
          onClick={saveIpRules}
          disabled={ipRulesSaving}
          className="bg-ink text-cream px-6 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-graphite transition-colors disabled:opacity-50"
        >
          {ipRulesSaving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {/* ── Blocage automatique (règles) ── */}
      <Link to="/admin/securite/blocage-automatique" data-testid="link-autoblock" className="block bg-ink text-cream border border-ink rounded-sm p-6 hover:bg-gold hover:text-ink transition-colors">
        <div className="flex items-center gap-2 mb-1"><ShieldAlert className="h-4 w-4" strokeWidth={1.5} /><h2 className="font-serif text-xl">Blocage automatique — règles</h2></div>
        <p className="text-sm opacity-90">
          Définissez des règles (pays + robot + cadence de requêtes, scanners, force brute…) qui bloquent automatiquement l'IP ou l'appareil,
          temporairement ou définitivement, avec mode simulation, liste blanche et journal des décisions.
        </p>
      </Link>

      {/* ── IPs bloquées manuellement ── */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldOff className="h-4 w-4 text-copper" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-ink">IPs bloquées</h2>
          </div>
          <p className="text-sm text-graphite">
            Liste des adresses IP bloquées manuellement. Pour ajouter une IP, utilisez le bouton <strong>Bloquer</strong> dans la vue <strong>Stats IP</strong>.
          </p>
        </div>

        {ipBlocksLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-sand animate-pulse rounded-sm" />)}
          </div>
        ) : ipBlocks.length === 0 ? (
          <p className="text-sm text-ash italic">Aucune IP bloquée manuellement.</p>
        ) : (
          <div className="divide-y divide-stone border border-stone rounded-sm overflow-hidden">
            {ipBlocks.map((entry) => (
              <div key={entry.ip} className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-sand/30 transition-colors">
                <span className="font-mono text-sm text-ink flex-1 flex items-center gap-2 flex-wrap">
                  {entry.ip}
                  {entry.bot_type && <BotBadge v={entry} compact />}
                  {entry.bot_source === "refus" && <span className="text-[10px] text-ash" title={entry.user_agent || undefined}>identifié sur requêtes refusées</span>}
                </span>
                {entry.reason && <span className="text-xs text-graphite truncate max-w-[160px]">{entry.reason}</span>}
                {entry.blocked_at && (
                  <span className="text-[11px] text-ash whitespace-nowrap">
                    {new Date(entry.blocked_at).toLocaleDateString("fr-FR")}
                  </span>
                )}
                <button
                  onClick={() => unblockIp(entry.ip)}
                  disabled={unblockingIp === entry.ip}
                  className="flex items-center gap-1.5 text-xs text-green-700 border border-green-300 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-50 shrink-0"
                >
                  <ShieldOff className="h-3.5 w-3.5" />
                  Débloquer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appareils bloqués (identifiant de session) */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-5" data-testid="session-blocks">
        <div>
          <h2 className="font-serif text-xl text-ink flex items-center gap-2"><Smartphone className="h-5 w-5 text-copper" /> Appareils bloqués</h2>
          <p className="text-sm text-graphite mt-1">
            Blocage par identifiant de session : il suit l'appareil même quand son adresse IP change (réseau mobile).
            Ajouté automatiquement lors du blocage d'une IP (appareils vus avec cette IP sur 30 jours) ou manuellement depuis le <strong>Parcours visiteur</strong>.
          </p>
        </div>

        {sessionBlocksLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-sand animate-pulse rounded-sm" />)}
          </div>
        ) : sessionBlocks.length === 0 ? (
          <p className="text-sm text-ash italic">Aucun appareil bloqué.</p>
        ) : (
          <div className="divide-y divide-stone border border-stone rounded-sm overflow-hidden">
            {sessionBlocks.map((entry) => {
              const DeviceIcon = entry.device === "Mobile" ? Smartphone : entry.device === "Tablette" ? Tablet : Monitor;
              return (
                <div key={entry.session_id} className="px-4 py-3 bg-white hover:bg-sand/30 transition-colors" data-testid={`session-block-${entry.session_id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs text-ink truncate max-w-[220px]" title={entry.session_id}>{entry.session_id}</span>
                    {entry.bot_type && <BotBadge v={entry} compact />}
                    {entry.propagated && (
                      <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" title={`Bloqué automatiquement avec l'IP ${entry.ip}`}>via IP {entry.ip}</span>
                    )}
                    <span className="flex-1" />
                    {entry.blocked_at && <span className="text-[11px] text-ash whitespace-nowrap">bloqué le {fmtTime(entry.blocked_at)}</span>}
                    <button
                      onClick={() => unblockDevice(entry.session_id)}
                      disabled={unblockingSid === entry.session_id}
                      className="flex items-center gap-1.5 text-xs text-green-700 border border-green-300 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-50 shrink-0"
                    >
                      <ShieldOff className="h-3.5 w-3.5" />
                      Débloquer
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] text-graphite">
                    <span className="inline-flex items-center gap-1"><DeviceIcon className="h-3 w-3 text-ash" /> {entry.device || "Appareil inconnu"}{entry.browser ? ` · ${entry.browser}` : ""}{entry.os ? ` · ${entry.os}` : ""}</span>
                    {(entry.country || entry.city) && <span>{flagEmoji(entry.country_code)} {entry.city ? `${entry.city}, ` : ""}{entry.country}</span>}
                    {entry.ip_history?.length > 0 && (
                      <span className="font-mono" title="Toutes les adresses IP vues pour cet appareil">
                        IP : {entry.ip_history.join(", ")}{entry.ip_history.length > 1 ? " (a changé d'IP)" : ""}
                      </span>
                    )}
                    {entry.last_seen && <span className="text-ash">dernière activité {fmtTime(entry.last_seen)}</span>}
                    {entry.user_email && <span className="text-ash">{entry.user_email}</span>}
                    {entry.blocked_by && <span className="text-ash">par {entry.blocked_by}</span>}
                    {entry.reason && !entry.propagated && <span className="text-ash truncate max-w-[260px]">{entry.reason}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Rétention & exports ── */}
      <div className="bg-white border border-stone rounded-sm p-6 space-y-4" data-testid="security-retention">
        <div className="flex items-center gap-2"><Trash2 className="h-4 w-4 text-copper" strokeWidth={1.5} /><h2 className="font-serif text-xl text-ink">Rétention & exports</h2></div>
        <p className="text-sm text-graphite">Pages vues, requêtes refusées, décisions automatiques et alertes lues sont purgées automatiquement chaque jour au-delà de la rétention. Les sessions visiteurs non converties inactives sont purgées au-delà de la rétention « sessions ».</p>
        {retention && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div><p className="text-xs uppercase tracking-[0.12em] text-ash mb-1">Journaux (jours)</p><input type="number" min="7" value={retention.days} onChange={(e) => setRetention({ ...retention, days: e.target.value })} className="w-full border border-stone rounded-sm px-3 py-2 text-sm bg-white" /></div>
            <div><p className="text-xs uppercase tracking-[0.12em] text-ash mb-1">Sessions visiteurs (jours)</p><input type="number" min="7" value={retention.sessions_days} onChange={(e) => setRetention({ ...retention, sessions_days: e.target.value })} className="w-full border border-stone rounded-sm px-3 py-2 text-sm bg-white" /></div>
          </div>
        )}
        {retention?.last_run && <p className="text-[11px] text-ash">Dernière purge : {fmtTime(retention.last_run)}{retention.last_result ? ` — ${Object.entries(retention.last_result).map(([k, v]) => `${k} ${v}`).join(", ")}` : ""}</p>}
        <div className="flex flex-wrap gap-2">
          <button onClick={saveRetention} disabled={retSaving || !retention} className="bg-ink text-cream px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">{retSaving ? "Enregistrement…" : "Enregistrer"}</button>
          <button onClick={purgeNow} disabled={purging} className="border border-red-200 text-red-600 bg-white px-4 py-2.5 text-xs uppercase tracking-[0.15em] hover:bg-red-50 rounded-sm disabled:opacity-60">{purging ? "Purge…" : "Purger maintenant"}</button>
          <span className="flex-1" />
          <button onClick={() => downloadCsv("/root/blocked-hits.csv?days=30", "requetes-refusees-30j.csv")} className="inline-flex items-center gap-1.5 border border-stone bg-white px-3 py-2.5 text-xs uppercase tracking-[0.1em] text-graphite hover:bg-sand rounded-sm"><Download className="h-3.5 w-3.5" /> Refus (CSV)</button>
          <button onClick={() => downloadCsv("/root/autoblock/events.csv?days=30", "decisions-automatiques-30j.csv")} className="inline-flex items-center gap-1.5 border border-stone bg-white px-3 py-2.5 text-xs uppercase tracking-[0.1em] text-graphite hover:bg-sand rounded-sm"><Download className="h-3.5 w-3.5" /> Décisions auto (CSV)</button>
        </div>
      </div>
    </div>
  );
}
